import React, { useEffect, useMemo, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import {
  CREATE_PICKER_REGISTRY,
  CreatePickerKind,
} from "@utils/createPickerRegistry";
import CreateOptionPickerCst from "./index";

let openKind: CreatePickerKind | null = null;
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => openKind;

export const openCreatePicker = (kind: CreatePickerKind) => {
  if (openKind === kind) return;
  openKind = kind;
  emit();
};

export const closeCreatePicker = () => {
  if (openKind === null) return;
  openKind = null;
  emit();
};

export const useCreatePickerOpenKind = () =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

export const canCreate = (
  user: { permissions?: string[] } | null | undefined,
  kind: CreatePickerKind,
): boolean =>
  !!user?.permissions?.includes(CREATE_PICKER_REGISTRY[kind].permission);

export const useCanCreate = (kind: CreatePickerKind): boolean => {
  const { user } = useAuth();
  return canCreate(user, kind);
};

export const CreatePickerHost: React.FC = () => {
  const kind = useCreatePickerOpenKind();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: corporateId = "" } = useParams();
  const { t } = useTranslation("create-picker");

  const allowed = !!kind && canCreate(user, kind);

  useEffect(() => {
    if (kind && !allowed) closeCreatePicker();
  }, [kind, allowed]);

  const def = kind ? CREATE_PICKER_REGISTRY[kind] : null;

  const options = useMemo(() => {
    if (!def) return [];
    return def.buildOptions({ navigate, corporateId, t }).map((option) => ({
      ...option,
      onSelect: () => {
        closeCreatePicker();
        option.onSelect();
      },
    }));
  }, [def, navigate, corporateId, t]);

  return (
    <CreateOptionPickerCst
      open={!!kind && allowed}
      onClose={closeCreatePicker}
      title={def ? t(def.titleKey) : ""}
      options={options}
    />
  );
};
